// Lightweight replacement for react-i18next: keeps the same call patterns.
// Namespace "mosque" resolves nested keys from locales/{ar|en}/mosque.json
// so labels can be edited in JSON without touching components.

import { useCallback, useContext, useMemo } from "react";
import { LanguageContext } from "../contexts/LanguageContext";
import arMosque from "../locales/ar/mosque.json";
import enMosque from "../locales/en/mosque.json";

const MOSQUE = { ar: arMosque, en: enMosque };

function getNested(obj, path) {
  if (!obj || path == null || path === "") return undefined;
  return path.split(".").reduce((acc, part) => {
    if (acc == null) return undefined;
    return acc[part];
  }, obj);
}

/** Sync lookup for mosque.json (use with useLanguage().language); not tied to useTranslation hook. */
export function getMosqueTranslation(lang, dottedKey) {
  const safe = lang === "en" ? "en" : "ar";
  const bundle = MOSQUE[safe] || MOSQUE.ar;
  const v = getNested(bundle, dottedKey);
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function normNamespaces(namespace) {
  if (Array.isArray(namespace)) return namespace;
  if (namespace) return [namespace];
  return [];
}

function interpolate(str, options) {
  if (!str || !options || typeof options !== "object") return str;
  let result = str;
  Object.keys(options).forEach((k) => {
    if (k === "defaultValue") return;
    result = result.replace(new RegExp(`{{${k}}}`, "g"), String(options[k]));
  });
  return result;
}

export function useTranslation(namespace) {
  const langCtx = useContext(LanguageContext);
  const language =
    (langCtx && langCtx.language) ||
    (typeof window !== "undefined" && localStorage.getItem("app_language")) ||
    "ar";
  const lang = language === "en" ? "en" : "ar";
  const nsList = normNamespaces(namespace);
  const useMosque = nsList.includes("mosque");

  const t = useCallback(
    (key, defaultValueOrOptions, options) => {
      let defaultValue = null;
      let opts = null;

      if (typeof defaultValueOrOptions === "string") {
        defaultValue = defaultValueOrOptions;
        opts = options;
      } else {
        opts = defaultValueOrOptions;
      }

      if (useMosque && typeof key === "string") {
        const bundle = MOSQUE[lang] || MOSQUE.ar;
        const resolved = getNested(bundle, key);
        if (typeof resolved === "string" && resolved.length > 0) {
          const interp =
            opts && typeof opts === "object" ? opts : null;
          return interpolate(resolved, interp);
        }
      }

      if (defaultValue) {
        let result = defaultValue;
        if (opts && typeof opts === "object") {
          result = interpolate(result, opts);
        }
        return result;
      }

      if (opts?.defaultValue) {
        let result = opts.defaultValue;
        if (opts && typeof opts === "object") {
          result = interpolate(result, opts);
        }
        return result;
      }

      const parts = key.split(".");
      return parts[parts.length - 1] || key;
    },
    [useMosque, lang]
  );

  const i18n = useMemo(
    () => ({
      language: lang,
      changeLanguage:
        langCtx && typeof langCtx.changeLanguage === "function"
          ? langCtx.changeLanguage
          : () => Promise.resolve(),
    }),
    [lang, langCtx]
  );

  return { t, i18n };
}
